import { useQuery } from '@tanstack/react-query';
import { fetchHookFees, fmtBps, pipsToBps, shortHex, summarizeFees, toolErrorText, tsToDate, type HookFeesPayload } from '../api/tools';
import { apiOn } from '../api/client';
import { CHAINS, MAINNET, TESTNET, type ChainId } from '../lib/chain';
import { fmtUtc } from '../lib/format';

export const FEES_EVERY_MS = 120000;
/** Rows shown per chain, newest first. */
export const FEES_ROWS = 25;

const txHref = (h: string, c: ChainId): string => `${CHAINS[c].explorer}/tx/${h}`;

function useFees(chainId: ChainId) {
  return useQuery<HookFeesPayload>({
    queryKey: ['hook-fees', chainId],
    queryFn: () => fetchHookFees(chainId),
    enabled: apiOn(),
    refetchInterval: FEES_EVERY_MS,
    retry: 1,
    refetchOnWindowFocus: false,
  });
}

const newestFirst = (p: HookFeesPayload) =>
  [...p.rows].sort((a, b) => (tsToDate(b.timestamp)?.getTime() ?? 0) - (tsToDate(a.timestamp)?.getTime() ?? 0));

function ChainFees({ chainId, title, empty }: { chainId: ChainId; title: string; empty: string }) {
  const q = useFees(chainId);
  let body;
  if (!apiOn()) body = <p className="table-intro">The live API is switched off for this page (?api=off).</p>;
  else if (q.isPending) body = <p className="table-intro">Loading the fee log…</p>;
  else if (q.isError) body = <p className="table-intro">{toolErrorText(q.error)}</p>;
  else if (q.data.indexed === false) body = <p className="table-intro">Not indexed yet: /api/data/hook/fees returns an empty state until the indexer switches to the schema with the BellHook fee events.</p>
  else if (q.data.rows.length === 0) body = <p className="table-intro">{empty}</p>;
  else {
    const rows = newestFirst(q.data).slice(0, FEES_ROWS);
    const summary = summarizeFees(q.data.rows);
    body = (
      <>
        <div className="table-scroll">
          <table className="basis">
            <thead><tr><th>Pool</th><th className="num">Swaps</th><th className="num">Median fee charged</th><th className="num">Max fee charged</th><th className="num">Quoted ≠ charged</th></tr></thead>
            <tbody>
              {summary.map((s) => (
                <tr key={s.poolId}><td title={s.poolId}>{shortHex(s.poolId)}</td><td className="num">{s.swaps}</td><td className="num">{s.medianSwapBps === null ? 'n/a' : fmtBps(s.medianSwapBps)}</td><td className="num">{s.maxSwapBps === null ? 'n/a' : fmtBps(s.maxSwapBps)}</td><td className="num">{s.mismatches}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="table-scroll">
          <table className="basis">
            <thead><tr><th>Time</th><th>Transaction</th><th>Pool</th><th className="num">Fee quoted</th><th className="num">Fee charged</th><th className="num">Amount in</th><th className="num">Amount out</th></tr></thead>
            <tbody>
              {rows.map((r) => {
                const d = tsToDate(r.timestamp);
                return (
                  <tr key={r.txHash + r.poolId}>
                    <td>{d ? fmtUtc(d) : 'n/a'}</td>
                    <td><a href={txHref(r.txHash, chainId)} rel="noopener">{shortHex(r.txHash)}</a></td>
                    <td title={r.poolId}>{shortHex(r.poolId)}</td>
                    <td className="num">{fmtBps(pipsToBps(r.quotedFeePips))}</td>
                    <td className="num">{fmtBps(pipsToBps(r.swapFeePips))}</td>
                    <td className="num">{String(r.amountIn)}</td>
                    <td className="num">{String(r.amountOut)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {q.data.rows.length > FEES_ROWS && <p className="fineprint">Newest {FEES_ROWS} of {q.data.rows.length} swaps; the summary covers all of them.</p>}
      </>
    );
  }
  return (
    <div className="fees-chain">
      <h3>{title}</h3>
      {body}
    </div>
  );
}

/** "Fee transparency (BellHook pools)": the swap fee log from GET /api/data/hook/fees per chain. */
export function FeeTransparency() {
  return (
    <section className="section" id="fees">
      <h2>Fee transparency (BellHook pools)</h2>
      <p className="table-intro">Every swap through a BellHook pool, with the fee the hook quoted before the swap and the fee the swap was charged, as indexed by the worker from chain events. Measured: read from the chain, not modeled. Amounts are raw token units. The verify and quote tools above are live; this log stays empty until the indexer switches to the schema with the BellHook fee events.</p>
      <ChainFees chainId={MAINNET} title="Mainnet 4663: NVDA/USDG" empty="No swaps yet. The mainnet pool is initialised and has no liquidity, so nothing has traded through it." />
      <ChainFees chainId={TESTNET} title="Testnet 46630: TSLA/mUSDG (the live demo)" empty="No swaps recorded on the testnet pool yet." />
    </section>
  );
}
